import { useMemo } from 'react';
import { get } from 'lodash-es';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { isEmptyPlaceholderProduct, isProductStillNeeded, prioritizeCatalogItems } from '../helpers';

export type VisibleProductListSortConfig = {
  value: 'fpIndex' | 'numberPrice';
  order: 'asc' | 'desc';
};

export const DEFAULT_SORT: VisibleProductListSortConfig = { value: 'fpIndex', order: 'asc' };

export type VisibleProductListResult = {
  available: ReadonlyArray<CookedProduct>;
  unavailable: ReadonlyArray<CookedProduct>;
};

function compareProducts(product1: CookedProduct, product2: CookedProduct, sortConfig: VisibleProductListSortConfig): number {
  const product1Val = get(product1, sortConfig.value, 0) as number;
  const product2Val = get(product2, sortConfig.value, 0) as number;
  return sortConfig.order === 'asc' ? product1Val - product2Val : product2Val - product1Val;
}

/**
 * Shared hook: same filter/sort as ProductListView. Returns available (still needed, not hidden, not an empty placeholder)
 * and unavailable lists. Use for both grid view and compact donation-fund view so they stay in sync.
 */
export const useVisibleProductList = (productList: ReadonlyArray<CookedProduct>, sortConfig: VisibleProductListSortConfig = DEFAULT_SORT): VisibleProductListResult => {
  return useMemo(() => {
    const prioritized = prioritizeCatalogItems([...productList]);
    const sorted = sortConfig.value === 'fpIndex' && sortConfig.order === 'asc' ? prioritized : [...prioritized].sort((a, b) => compareProducts(a, b, sortConfig));

    const acc = { available: [] as CookedProduct[], unavailable: [] as CookedProduct[] };
    for (const product of sorted) {
      if (product.isHidden || isEmptyPlaceholderProduct(product)) continue;
      if (isProductStillNeeded(product)) {
        acc.available.push(product);
      } else {
        acc.unavailable.push(product);
      }
    }
    return acc;
  }, [productList, sortConfig]);
};
